import { useState } from "react";
import { toast } from "sonner";
import { FileText, Eye, Download, Share2, Copy, MessageCircle, Facebook, Twitter, Linkedin, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { pkr } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;

const PosterTile = ({ d, base, size, label, hint }) => {
  const [busy, setBusy] = useState(false);
  const src = base.replace("/pdf", "/poster") + (base.includes("?") ? "&" : "?") + `size=${size}`;
  const share = async () => {
    setBusy(true);
    try {
      const blob = await fetch(src).then((r) => r.blob());
      const file = new File([blob], `${d.ref}_${size}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text: `${d.name} — ${pkr(d.price)}` });
      else toast.info("Your browser can't share images directly. Download the poster and post it from your phone.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share the poster"); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid={`poster-${size}`}>
      <a href={src} target="_blank" rel="noreferrer" className={`block overflow-hidden rounded-lg border bg-slate-100 ${size === "story" ? "aspect-[9/16]" : "aspect-square"}`}>
        <img src={src} alt={`${label} poster`} className="h-full w-full object-cover" loading="lazy" />
      </a>
      <p className="text-xs font-semibold text-slate-700">{label} <span className="font-normal text-slate-500">{hint}</span></p>
      <div className="grid grid-cols-2 gap-1.5">
        <Button size="sm" onClick={share} disabled={busy} data-testid={`poster-share-${size}`}><Share2 className="mr-1 h-3.5 w-3.5" />Share</Button>
        <Button asChild size="sm" variant="outline"><a href={`${src}&download=true`} data-testid={`poster-download-${size}`}><Download className="mr-1 h-3.5 w-3.5" />Save</a></Button>
      </div>
    </div>
  );
};

export const DealPdfShareDialog = ({ d, trigger }) => {
  const { user } = useAuth();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const onOpen = async (o) => {
    if (!o || url) return;
    try { const { data } = await api.get(`/deals/${d.id}/share-link`); setUrl(ORIGIN + data.path); } catch (e) { toast.error(errMsg(e)); }
  };
  const text = `${d.name} — ${pkr(d.price)} (fixed price). See full details:`;
  const enc = encodeURIComponent;
  const sep = url.includes("?") ? "&" : "?";
  const links = [
    ["WhatsApp", MessageCircle, `https://wa.me/?text=${enc(`${text} ${url}`)}`, "bg-[#25D366] text-white hover:bg-[#1ebe57]"],
    ["Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, "bg-[#1877F2] text-white hover:bg-[#0f63d1]"],
    ["X", Twitter, `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`, "bg-black text-white hover:bg-slate-800"],
    ["LinkedIn", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, "bg-[#0A66C2] text-white hover:bg-[#0956a3]"],
    ["Telegram", Send, `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`, "bg-[#229ED9] text-white hover:bg-[#1a8bc2]"],
  ];
  const shareFile = async () => {
    setBusy(true);
    try {
      const blob = await fetch(url).then((r) => r.blob());
      const file = new File([blob], `${d.ref}.pdf`, { type: "application/pdf" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text: `${d.name} — ${pkr(d.price)}` });
      else toast.info("Your browser can't share files directly. Use the buttons below or download the PDF.");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share the PDF"); }
    setBusy(false);
  };
  return (
    <Dialog onOpenChange={onOpen}>
      <DialogTrigger asChild>{trigger || <Button variant="outline" data-testid={`deal-pdf-btn-${d.ref}`}><FileText className="mr-2 h-4 w-4" />PDF &amp; Share</Button>}</DialogTrigger>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Share this deal</DialogTitle></DialogHeader>
        {!url ? <p className="py-6 text-center text-sm text-slate-500">Preparing…</p> : (
          <div className="space-y-4" data-testid="deal-pdf-dialog">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Image posters</p>
            <div className="grid grid-cols-2 gap-3">
              <PosterTile d={d} base={url} size="square" label="Square" hint="Instagram / Facebook post" />
              <PosterTile d={d} base={url} size="story" label="Story" hint="WhatsApp Status / Stories" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">PDF deal sheet</p>
            <p className="text-sm text-slate-600">A customer-friendly PDF and posters with photo, fixed price and details. Commission is <b>not</b> shown. {user?.role === "executive" ? "Your name, phone and Sales ID are included so customers can contact you." : "No executive contact is added to the admin version."}</p>
            <div className="grid grid-cols-2 gap-2">
              <Button asChild variant="outline" className="h-11"><a href={url} target="_blank" rel="noreferrer" data-testid="deal-pdf-view"><Eye className="mr-2 h-4 w-4" />View PDF</a></Button>
              <Button asChild variant="outline" className="h-11"><a href={`${url}${sep}download=true`} data-testid="deal-pdf-download"><Download className="mr-2 h-4 w-4" />Download</a></Button>
            </div>
            <Button className="h-11 w-full" onClick={shareFile} disabled={busy} data-testid="deal-pdf-share-file"><Share2 className="mr-2 h-4 w-4" />{busy ? "Preparing…" : "Share PDF file (phone)"}</Button>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Share link on</p>
            <div className="grid grid-cols-3 gap-2">
              {links.map(([name, Icon, href, cls]) => (
                <a key={name} href={href} target="_blank" rel="noreferrer" data-testid={`deal-pdf-share-${name.toLowerCase()}`}
                  className={`flex h-11 items-center justify-center gap-2 rounded-md text-sm font-medium ${cls}`}><Icon className="h-4 w-4" />{name}</a>
              ))}
              <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} data-testid="deal-pdf-copy-link"
                className="flex h-11 items-center justify-center gap-2 rounded-md border text-sm font-medium hover:bg-slate-50"><Copy className="h-4 w-4" />Copy link</button>
            </div>
            <p className="text-xs text-slate-500">For Instagram or TikTok, download the PDF or use "Share PDF file" on your phone.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
